import { useEffect, useState } from 'preact/hooks';
import { ChevronRight, ScrollText, X } from 'lucide-preact';
import { t } from '../i18n';
import { JourneyTimeline, useJourneyRows } from './JourneyTimeline';

/**
 * Journey-log entry point in the drawer. Instead of spilling the full event
 * list into the bottom sheet (#180), we surface a single compact row that
 * opens the log in its own full-screen view. Keeps the drawer scannable —
 * addresses and stats stay above the fold — while a long journey's history
 * gets a dedicated, scrollable surface.
 *
 * Renders nothing when the route has no describable events, so pre-#180
 * journeys keep the compact drawer untouched.
 */
export function JourneyLog() {
  const rows = useJourneyRows();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (rows.length === 0) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        class="flex w-full items-center gap-2.5 rounded-xl bg-surface-muted px-3 py-2.5 text-left transition-colors hover:bg-surface-muted/70"
      >
        <span class="text-primary shrink-0" aria-hidden="true">
          <ScrollText size={16} />
        </span>
        <span class="min-w-0 flex-1 text-sm font-medium text-text">{t('timeline.title')}</span>
        <span class="shrink-0 rounded-full bg-surface px-2 py-0.5 text-xs font-medium tabular-nums text-text-secondary">
          {rows.length}
        </span>
        <span class="text-text-secondary shrink-0" aria-hidden="true">
          <ChevronRight size={16} />
        </span>
      </button>

      {open && (
        <div
          class="pointer-events-auto fixed inset-0 z-30 flex flex-col bg-surface motion-safe:animate-[fadeIn_0.2s_ease-out]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="journey-log-heading"
        >
          <header class="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
            <h2 id="journey-log-heading" class="text-base font-semibold text-text">
              {t('timeline.title')}
            </h2>
            <button
              type="button"
              onClick={() => setOpen(false)}
              class="inline-flex h-9 w-9 items-center justify-center rounded-full text-text-secondary hover:bg-surface-muted hover:text-text transition-colors"
              aria-label={t('timeline.close')}
            >
              <X size={20} aria-hidden="true" />
            </button>
          </header>
          <div class="flex-1 overflow-y-auto px-4 py-4">
            <JourneyTimeline />
          </div>
        </div>
      )}
    </>
  );
}
